/**
 * The top bar's time-zone switch (Galley #21943). It always shows the
 * zone times are shown in ("🕐 GMT+2", "🕐 UTC"), and opens two choices:
 * your own time zone (the default, as before) or UTC. The choice is kept
 * per browser (`lib/timezone.ts`). One line under the choices says that
 * day buckets are UTC days in either mode, because the server cuts them
 * at 00:00 UTC.
 */
import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import {
  localUtcOffsetLabel,
  localZoneName,
  setTimeZonePref,
  useTimeZonePref,
  UTC_DAYS_NOTE,
  zoneAbbrev,
  type TimeZonePref,
} from "@/lib/timezone";

/**
 * `variant`: "button" (a bordered button, beside Look up, on phones) or
 * "line" (one small line under Subscriptions in the session column on
 * wider screens, so no nav item moves to make room).
 */
export function TimeZoneSwitch({
  variant = "button",
}: {
  variant?: "button" | "line";
}) {
  const pref = useTimeZonePref();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const now = new Date();
  const localName = localZoneName();
  const localAbbrev = zoneAbbrev(now, "local");
  const shown = zoneAbbrev(now, pref);
  // "GMT+2" already is the offset; "EDT" is not, so it gets "UTC-4" too.
  const offset = /^(GMT|UTC)([+-]|$)/.test(localAbbrev)
    ? ""
    : localUtcOffsetLabel(now);
  const localLabel = `${localName || "this browser's zone"} (${[localAbbrev, offset]
    .filter(Boolean)
    .join(", ")})`;
  const summary =
    pref === "utc"
      ? "Times on this dashboard are shown in UTC."
      : `Times on this dashboard are shown in your time zone, ${localLabel}.`;

  const choose = (next: TimeZonePref) => {
    setOpen(false);
    setTimeZonePref(next);
  };

  const options: Array<{ value: TimeZonePref; label: string }> = [
    { value: "local", label: `Your time zone: ${localLabel}` },
    { value: "utc", label: "UTC" },
  ];

  return (
    <div
      className="relative"
      ref={rootRef}
      data-testid={`time-zone-switch-${variant}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Time zone: ${shown}. ${summary} Change it`}
        title={`${summary} Click to change.`}
        className={
          variant === "line"
            ? "inline-flex items-center gap-1 text-[11px] leading-tight text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-500 dark:text-gray-400 dark:hover:text-gray-200"
            : "inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        }
      >
        <span aria-hidden="true">🕐</span>
        {variant === "line" && <span>Times:</span>}
        <span data-testid="time-zone-switch-label">{shown}</span>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Show times in"
          className="absolute right-0 z-50 mt-1 w-72 rounded-md border border-gray-200 bg-white p-2 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Show times in
          </p>
          {options.map((opt) => {
            const active = pref === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => choose(opt.value)}
                className={cn(
                  "flex w-full items-start gap-2 rounded px-2 py-1.5 text-left",
                  active
                    ? "bg-blue-50 font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
                )}
              >
                <span aria-hidden="true" className="w-3 shrink-0">
                  {active ? "✓" : ""}
                </span>
                <span>{opt.label}</span>
              </button>
            );
          })}
          <p className="mt-1 border-t border-gray-100 px-2 pt-2 text-[11px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
            {UTC_DAYS_NOTE} Kept for this browser.
          </p>
        </div>
      )}
    </div>
  );
}
